import { useCallback, useEffect, useMemo, useRef } from "react";
import { AgGrid, Flex, Grid, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { HighlightText } from "~/pages/Reporting/components/HighlightText";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { useAgGridDefaults } from "~/pages/UserAndRoles/utils/gridConfig";
import { SPACING } from "~/styles/variables";
import { compareDateStrings } from "../sorting";
import { noRowsOverlayIcon } from "./styles_vanilla.css";

const FORMULA_DATE_TIME_FORMAT = "MMM d, yyyy h:mma";

export type Formula = {
  id: string;
  name: string;
  description: string;
  formula: string;
  isActive: boolean;
  /** Raw ISO timestamp; formatted for display via `valueGetter`. */
  lastUpdated: string | null;
  updatedByFullName: string | null;
  /** Raw ISO timestamp; formatted for display via `valueGetter`. */
  archivedAt: string | null;
  archivedByFullName: string | null;
};

const formatDateWithBy = (
  timestamp: string | null | undefined,
  by: string | null | undefined,
  formatDateWithTimezone: (date: string, format: string) => string,
): string => {
  const formatted = timestamp
    ? formatDateWithTimezone(timestamp, FORMULA_DATE_TIME_FORMAT)
    : "";
  const trimmedBy = by?.trim();
  if (formatted && trimmedBy) return `${formatted} by ${trimmedBy}`;
  return formatted || trimmedBy || "";
};

type FormulasGridProps = {
  formulas: Formula[];
  searchValue?: string;
  viewFilter?: "active" | "archived";
  onEditFormula?: (formulaId: string) => void;
};

type GridContext = {
  searchValue: string;
  viewFilter: "active" | "archived";
};

const FormulasNoRowsOverlay = (params: AgGrid.INoRowsOverlayParams<Formula, GridContext>) => {
  const isArchived = params.context?.viewFilter === "archived";
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={SPACING.X_SMALL}
    >
      <Icon
        type="function"
        size="xx-large"
        color={Colors.Neutral600}
        className={noRowsOverlayIcon}
      />
      <Text variant="h4" align="center" color={Colors.Neutral700}>
        {isArchived ? "No archived formulas yet" : "No formulas yet"}
      </Text>
    </Flex>
  );
};

const HighlightCellRenderer = (
  params: AgGrid.ICellRendererParams<Formula, string, GridContext>,
) => {
  const searchValue = params.context?.searchValue ?? "";
  return <HighlightText text={params.value ?? ""} searchValue={searchValue} />;
};

const nodeMatchesSearch = (formula: Formula, searchTerm: string): boolean => {
  const lowerSearch = searchTerm.toLowerCase();
  return (
    formula.name.toLowerCase().includes(lowerSearch) ||
    formula.description.toLowerCase().includes(lowerSearch) ||
    formula.formula.toLowerCase().includes(lowerSearch)
  );
};

export const FormulasGrid = ({
  formulas,
  searchValue = "",
  viewFilter = "active",
  onEditFormula,
}: FormulasGridProps) => {
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();

  const rowData = useMemo<Formula[]>(() => {
    if (!searchValue.trim()) {
      return formulas;
    }
    return formulas.filter((formula) => nodeMatchesSearch(formula, searchValue));
  }, [formulas, searchValue]);

  const columnDefs = useMemo<AgGrid.ColDef<Formula>[]>(() => {
    const isArchivedView = viewFilter === "archived";
    return [
      {
        field: "name",
        headerName: "Formula name",
        sortable: true,
        flex: 1,
        cellRenderer: HighlightCellRenderer,
      },
      {
        field: "description",
        headerName: "Description",
        sortable: true,
        flex: 1.5,
        cellRenderer: HighlightCellRenderer,
      },
      {
        field: "formula",
        headerName: "Formula",
        sortable: true,
        flex: 2,
        cellRenderer: HighlightCellRenderer,
        wrapText: true,
        autoHeight: true,
      },
      {
        colId: isArchivedView ? "archivedAt" : "lastUpdated",
        field: isArchivedView ? "archivedAt" : "lastUpdated",
        headerName: isArchivedView ? "Archived date" : "Last updated",
        sortable: true,
        flex: 1.5,
        cellRenderer: HighlightCellRenderer,
        valueGetter: (params) =>
          isArchivedView
            ? formatDateWithBy(
                params.data?.archivedAt,
                params.data?.archivedByFullName,
                formatDateWithTimezone,
              )
            : formatDateWithBy(
                params.data?.lastUpdated,
                params.data?.updatedByFullName,
                formatDateWithTimezone,
              ),
        comparator: (_, __, nodeA, nodeB) =>
          isArchivedView
            ? compareDateStrings(nodeA.data?.archivedAt, nodeB.data?.archivedAt)
            : compareDateStrings(nodeA.data?.lastUpdated, nodeB.data?.lastUpdated),
      },
    ];
  }, [viewFilter, formatDateWithTimezone]);

  const context = useMemo<GridContext>(
    () => ({
      searchValue,
      viewFilter,
    }),
    [searchValue, viewFilter],
  );

  const gridRef = useRef<AgGrid.GridApi<Formula> | null>(null);

  const handleGridReady = useCallback((params: AgGrid.GridReadyEvent<Formula>) => {
    gridRef.current = params.api;
  }, []);

  const handleRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<Formula>) => {
      if (event.data && event.data.isActive !== false) {
        onEditFormula?.(event.data.id);
      }
    },
    [onEditFormula],
  );

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.refreshCells({ force: true });
    }
  }, [searchValue]);

  const defaultSortField = viewFilter === "archived" ? "archivedAt" : "lastUpdated";

  useEffect(() => {
    gridRef.current?.applyColumnState({
      state: [{ colId: defaultSortField, sort: "desc" }],
      defaultState: { sort: null },
    });
  }, [defaultSortField]);

  const { gridOptions } = useAgGridDefaults({
    overrides: {
      domLayout: "autoHeight",
      rowHeight: 50,
      suppressContextMenu: true,
      suppressCellFocus: true,
      suppressHorizontalScroll: true,
      pagination: false,
      sideBar: false,
      statusBar: undefined,
    },
  });

  return (
    <Grid<Formula>
      rowData={rowData}
      getRowId={(params) => params.data?.id ?? ""}
      columnDefs={columnDefs}
      gridOptions={gridOptions}
      context={context}
      onGridReady={handleGridReady}
      onRowClicked={handleRowClicked}
      noRowsOverlayComponent={FormulasNoRowsOverlay}
      initialState={{
        sort: {
          sortModel: [{ colId: defaultSortField, sort: "desc" }],
        },
      }}
    />
  );
};
